import * as THREE from 'three';
import { L } from '../game/config';

/** Shared uniforms for every custom FX shader (one object, referenced by all materials). */
export const fxUniforms = {
  uTime: { value: 0 },
  uFogDensity: { value: 0.021 },
};

/** World x from a view-space position (the view matrix is rigid: world = Rᵀ(mv - t)). */
export const FOG_VERT = /* glsl */ `varying float vFogDepth; varying float vFogX;
  #define FOG_FROM_MV(mv) vFogDepth = -(mv).z; vFogX = (((mv).xyz - viewMatrix[3].xyz) * mat3(viewMatrix)).x;`;
/** Scene fog plus the spawn fog bank: effects inside the bank sink into it like the enemies do. */
export const FOG_FRAG = /* glsl */ `
  varying float vFogDepth; varying float vFogX;
  uniform float uFogDensity;
  float fogKeep() {
    float d = uFogDensity * vFogDepth;
    float bank = 1.0 - smoothstep(${L.fogStartX.toFixed(2)}, ${(L.fogEndX + 0.6).toFixed(2)}, vFogX);
    // HDR effects survive a mild dim through tone mapping, so the bank swallows most of their light
    return exp(-d * d) * (1.0 - bank * 0.95);
  }
`;

/** Unit quad (-.5..+.5) as instanced base geometry. */
export function quadGeometry(): THREE.InstancedBufferGeometry {
  const g = new THREE.InstancedBufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0], 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
  g.setIndex([0, 1, 2, 0, 2, 3]);
  return g;
}

/** Tracks a dirty [min,max) instance range so we upload only what changed. */
export class DirtyRange {
  min = Infinity; max = -1;
  mark(i: number) { if (i < this.min) this.min = i; if (i > this.max) this.max = i; }
  flush(attrs: THREE.InstancedBufferAttribute[]) {
    if (this.max < 0) return;
    for (const a of attrs) {
      a.clearUpdateRanges();
      a.addUpdateRange(this.min * a.itemSize, (this.max - this.min + 1) * a.itemSize);
      a.needsUpdate = true;
    }
    this.min = Infinity; this.max = -1;
  }
}
